import XSelect from '@/Mantine/XSelect'
import XTextInput from '@/Mantine/XTextInput'
import { RootState } from '@/Store'
import { updateCurrentStep, updateLead } from '@/Store/leadFormSlice'
import { DocumentTypeProps } from '@/Types/DocumentTypes'
import { Button, Divider, HStack, Radio, RadioButton, Typography, VStack, XelaColor } from '@/XelaReact'
import FileUpload from '@/XelaReact/FileUpload/FileUpload'
import { Box, LoadingOverlay } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'
import axios from 'axios'
import collect from 'collect.js'
import { FC, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Lead = App.Models.Lead

interface FormData {
  document: File | null
  documents: File[]
  name: string
  group: string
  type: string
  others_type: string
  entity: string
  entity_id: string | null
  lead_id: string | null
  belongs_to: string
}

const initialData: FormData = {
  document: null,
  documents: [],
  name: '',
  group: '',
  type: '',
  belongs_to: 'Applicant',
  others_type: '',
  entity: 'Lead',
  entity_id: null,
  lead_id: null,
}

const UPLOAD_TYPES = [
  {
    name: 'single_upload',
    label: 'Single Upload',
  },
  {
    name: 'bulk_upload',
    label: 'Bulk Upload',
  },
]

interface UploadPayload {
  lead: Lead
}

const LeadFormDocumentsUploadDetail: FC<{ fields: string[]; agencyId: number }> = ({
  fields,
  agencyId,
}) => {
  const dispatch = useDispatch()
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [formData, setFormData] = useState<FormData>(initialData)
  const [loading, setLoading] = useState(false)
  const [uploadType, setUploadType] = useState('single_upload')
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeProps[]>([])
  const [isDocumentAdded, setIsDocumentAdded] = useState(false)
  const [isDocumentUploaded, setIsDocumentUploaded] = useState(false)

  const leadStore = useSelector((state: RootState) => state.leadForm)

  useEffect(() => {
    if (!fields.includes('single_upload')) {
      UPLOAD_TYPES.splice(
        UPLOAD_TYPES.findIndex((type) => type.name === 'single_upload'),
        1
      )
      setUploadType('bulk_upload')
    }
    if (!fields.includes('bulk_upload')) {
      UPLOAD_TYPES.splice(
        UPLOAD_TYPES.findIndex((type) => type.name === 'bulk_upload'),
        1
      )
      setUploadType('single_upload')
    }
  }, [])

  useEffect(() => {
    axios
      .post<DocumentTypeProps[]>('/guest/select/lead-meta-options', {
        entity_name: 'documents',
        column_name: 'lead_documents',
        with_group: true,
        agency_id: agencyId,
      })
      .then((response) => {
        setDocumentTypes(response.data)
      })
      .catch((error) => {
        console.error('Error fetching documents types:', error)
      })
  }, [])

  useEffect(() => {
    if (leadStore.lead) {
      const lead = leadStore.lead
      setFormData((prevData) => ({
        ...prevData,
        lead_id: lead.id.toString(),
        entity_id: lead.id.toString(),
      }))
    }
  }, [leadStore.lead])

  const handleUploadSuccess = (action: string, payload: UploadPayload) => {
    if (payload.lead) {
      dispatch(updateLead(payload.lead))
    }

    if (action === 'documents_upload:single_upload') {
      showNotification({
        withCloseButton: true,
        autoClose: 5000,
        title: 'Success',
        message: 'Document uploaded successfully',
        color: 'green',
        icon: <IconCheck />,
      })
    }

    if (action === 'documents_upload:bulk_upload') {
      showNotification({
        withCloseButton: true,
        autoClose: 5000,
        title: 'Success',
        message: 'Documents uploaded successfully',
        color: 'green',
        icon: <IconCheck />,
      })
    }

    if (action === 'documents_upload:saved') {
      showNotification({
        withCloseButton: true,
        autoClose: 5000,
        title: 'Success',
        message: 'Documents saved successfully',
        color: 'green',
        icon: <IconCheck />,
      })
      dispatch(updateCurrentStep(leadStore.current_step + 1))
    }

    setIsDocumentAdded(false)
    setFormData(initialData)
  }

  const fileUploadHandler = useCallback(
    async (isSaving = false) => {
      if (!leadStore.lead_id || loading || !formData.document) return

      setLoading(true)
      try {
        const formDataToSend = new FormData()
        formDataToSend.append('document', formData.document)
        formDataToSend.append('name', formData.name)
        formDataToSend.append('group', formData.group)
        formDataToSend.append('type', formData.type)
        formDataToSend.append('others_type', formData.others_type)
        formDataToSend.append('entity', formData.entity)
        formDataToSend.append('entity_id', formData.entity_id || '')
        formDataToSend.append('lead_id', formData.lead_id || '')
        formDataToSend.append('belongs_to', formData.belongs_to)
        formDataToSend.append('uuid', leadStore.lead_form_uuid)
        formDataToSend.append('csrfToken', leadStore.csrfToken)

        const response = await axios.post(
          `/leads/form/${leadStore.lead_id}/upload-document?is_saving=${isSaving}`,
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        const { action, payload } = response.data
        handleUploadSuccess(action, payload)
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          setErrors(error.response.data.errors || {})
        }
      } finally {
        setLoading(false)
        setIsDocumentAdded(false)
        setIsDocumentUploaded(true)
      }
    },
    [formData, leadStore.lead_id, loading]
  )

  const bulkFileUploadHandler = useCallback(
    async (isSaving = false) => {
      if (!leadStore.lead_id || loading || formData.documents.length === 0) return

      setLoading(true)
      try {
        const formDataToSend = new FormData()
        formData.documents.forEach((doc) => {
          formDataToSend.append('documents[]', doc)
        })
        formDataToSend.append('entity', formData.entity)
        formDataToSend.append('entity_id', formData.entity_id || '')
        formDataToSend.append('lead_id', formData.lead_id || '')
        formDataToSend.append('belongs_to', formData.belongs_to)
        formDataToSend.append('uuid', leadStore.lead_form_uuid)
        formDataToSend.append('csrfToken', leadStore.csrfToken)

        const response = await axios.post(
          `/leads/form/${leadStore.lead_id}/bulk-upload-documents?is_saving=${isSaving}`,
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        const { action, payload } = response.data
        handleUploadSuccess(action, payload)
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          setErrors(error.response.data.errors || {})
        }
      } finally {
        setLoading(false)
        setIsDocumentAdded(false)
        setIsDocumentUploaded(true)
      }
    },
    [formData, leadStore.lead_id, loading]
  )

  return (
    <>
      {leadStore.is_mobile_view && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, width: '100vw', zIndex: 9999 }}>
          <HStack justifyContent="flex-end">
            <Button
              style={{
                borderRadius: 0,
                width: '100%',
                border: '1px solid #8E8C94 !important',
                background: '#FFFFFF',
                outline: 'none !important',
              }}
              label={'Back'}
              variant="secondary"
              onClick={() => {
                dispatch(updateCurrentStep(leadStore.current_step - 1))
              }}
            ></Button>
            <Button
              style={{
                borderRadius: 0,
                width: '100%',
                border: '1px solid #003CFF !important',
                outline: 'none !important',
              }}
              label={leadStore.current_step === leadStore.total_step - 1 ? 'Save' : 'Save & Next'}
              onClick={() => {
                if (isDocumentAdded && !isDocumentUploaded) {
                  if (uploadType === 'single_upload') {
                    fileUploadHandler(true)
                  } else {
                    bulkFileUploadHandler(true)
                  }
                } else {
                  dispatch(updateCurrentStep(leadStore.current_step + 1))
                }
              }}
            ></Button>
          </HStack>
        </div>
      )}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
      >
        <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ color: 'blueX' }} />
        <VStack spacing="12px" height="100%">
          <VStack
            spacing="12px"
            style={{
              height: '100%',
              overflowY: 'auto',
            }}
          >
            <VStack spacing="12px">
              <HStack spacing="12px" style={{ flexWrap: 'wrap' }}>
                <HStack spacing="12px" justifyContent="flex-start">
                  <RadioButton
                    checkedValue={uploadType}
                    direction="horizontal"
                    border={true}
                    size={'small'}
                    onChange={(e) => {
                      const target: HTMLInputElement = e.target as HTMLInputElement
                      if (target) {
                        if (target.value === 'bulk_upload') {
                          setFormData((prevData) => ({
                            ...prevData,
                            document: null,
                            name: '',
                            group: '',
                            type: '',
                            others_type: '',
                            documents: [],
                          }))
                        } else {
                          setFormData((prevData) => ({
                            ...prevData,
                            documents: [],
                          }))
                        }
                        setUploadType(target.value)
                      }
                    }}
                    radios={UPLOAD_TYPES.map((selection) => new Radio(selection.name, selection.label))}
                    name={'upload_radios'}
                  />
                </HStack>
              </HStack>
              <HStack>
                <Divider variant={'dotted'}></Divider>
              </HStack>
              <HStack spacing="12px" style={{ minHeight: '240px' }}>
                {uploadType === 'single_upload' && (
                  <VStack spacing="12px">
                    <HStack>
                      <FileUpload
                        height="200px"
                        error={errors['document'] !== undefined}
                        helperText={errors['document']}
                        value={formData.document}
                        typeUploadText="PNG, JPG, PDF, DOCX, XLSX (max. 200 MB)"
                        options={{
                          onDrop: (acceptedFiles: File[]) => {
                            if (acceptedFiles.length > 0) {
                              setIsDocumentAdded(true)
                              setIsDocumentUploaded(false)
                              setFormData((prevData) => ({
                                ...prevData,
                                document: acceptedFiles[0],
                                name: acceptedFiles[0].name,
                              }))
                            }
                          },
                        }}
                      ></FileUpload>
                    </HStack>

                    <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                      <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                        <XSelect
                          searchable
                          label={'Type'}
                          error={errors.type}
                          value={formData.type}
                          data={documentTypes}
                          onChange={(value) => {
                            if (value) {
                              const datum = collect(documentTypes).where('value', value).first()
                              if (datum) {
                                setFormData((prevData) => ({
                                  ...prevData,
                                  group: datum.group,
                                  type: value,
                                }))
                              }
                            }
                          }}
                        />
                      </HStack>

                      <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                        <XTextInput
                          label="Name"
                          value={formData.name}
                          error={errors['name']}
                          onChange={(e) => {
                            if (e.target) {
                              setFormData((prevData) => ({
                                ...prevData,
                                name: e.target.value,
                              }))
                            }
                          }}
                        />
                      </HStack>

                      <HStack width="initial" justifyContent={'flex-end'}>
                        <Button
                          style={{ width: '80px' }}
                          label={'Upload'}
                          onClick={() => {
                            if (leadStore.lead_id && !loading && formData.document) {
                              fileUploadHandler()
                            }
                          }}
                        />
                      </HStack>
                    </HStack>
                  </VStack>
                )}

                {uploadType === 'bulk_upload' && (
                  <VStack spacing="12px">
                    <HStack>
                      <FileUpload
                        height="200px"
                        error={Object.values(errors).length > 0}
                        helperText={Object.values(errors).join(', <br/>')}
                        typeUploadText="PDF,JPG,PNG,WORD,XLSX (max. 200 MB)"
                        options={{
                          onDrop: (acceptedFiles: File[]) => {
                            if (acceptedFiles.length > 0) {
                              setIsDocumentAdded(true)
                              setIsDocumentUploaded(false)
                              setFormData((prev) => ({
                                ...prev,
                                documents: [...prev.documents, ...acceptedFiles],
                              }))
                            }
                          },
                        }}
                        value={null}
                      ></FileUpload>
                    </HStack>
                    <HStack justifyContent="flex-end">
                      <Button
                        style={{ width: '80px' }}
                        label={'Upload'}
                        onClick={() => {
                          if (leadStore.lead_id && !loading && formData.documents.length > 0) {
                            bulkFileUploadHandler()
                          }
                        }}
                      />
                    </HStack>
                    {formData.documents.length > 0 && (
                      <>
                        <HStack>
                          <Divider variant={'dotted'}></Divider>
                        </HStack>
                        <VStack
                          spacing="4px"
                          style={{
                            height: '150px',
                            overflowY: 'auto',
                            padding: '4px',
                          }}
                        >
                          {formData.documents.map((file, index) => (
                            <HStack key={index} justifyContent="space-between">
                              <Typography
                                variant={'body-small-bold'}
                                style={{ width: '400px', wordBreak: 'break-all' }}
                              >
                                {index + 1}. {file.name}
                              </Typography>
                              <Box
                                style={{
                                  padding: '4px',
                                  cursor: 'pointer',
                                }}
                              >
                                <IconX
                                  size={16}
                                  color={XelaColor.Red6}
                                  onClick={() => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      documents: prev.documents.filter((_, i) => i !== index),
                                    }))
                                  }}
                                />
                              </Box>
                            </HStack>
                          ))}
                        </VStack>
                      </>
                    )}
                  </VStack>
                )}
              </HStack>
            </VStack>
          </VStack>
          {!leadStore.is_mobile_view && (
            <>
              <HStack>
                <Divider variant={'dotted'}></Divider>
              </HStack>
              <HStack spacing={'16px'} justifyContent="flex-end">
                <Button
                  label={'Back'}
                  variant="secondary"
                  onClick={() => {
                    dispatch(updateCurrentStep(leadStore.current_step - 1))
                  }}
                ></Button>
                <Button
                  label={leadStore.current_step === leadStore.total_step - 1 ? 'Save' : 'Save & Next'}
                  onClick={() => {
                    if (isDocumentAdded && !isDocumentUploaded) {
                      if (uploadType === 'single_upload') {
                        fileUploadHandler(true)
                      } else {
                        bulkFileUploadHandler(true)
                      }
                    } else {
                      dispatch(updateCurrentStep(leadStore.current_step + 1))
                    }
                  }}
                ></Button>
              </HStack>
            </>
          )}
        </VStack>
      </div>
    </>
  )
}

export default LeadFormDocumentsUploadDetail
